'use client';

import { useRef, useState } from 'react';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Portal from '@mui/material/Portal';
import { type Variants, motion, useMotionValue, useSpring } from 'framer-motion';
import { useTranslations } from 'next-intl';
import Image from 'next/image';

import External from '@/components/SvgIcons/External';
import { Link } from '@/i18n/navigation';

import styles from './CaseStudyCard.module.scss';

const EASE = [0.22, 1, 0.36, 1] as const;

const cardVariants: Variants = {
  idle: {
    y: 0,
    scale: 1,
    transition: { duration: 0.26, ease: EASE },
  },
  hover: {
    y: -10,
    scale: 1.02,
    transition: { duration: 0.26, ease: EASE },
  },
};

const chipVariants: Variants = {
  idle: {
    opacity: 0,
    transition: { duration: 0.22, ease: EASE },
  },
  hover: {
    opacity: 1,
    transition: { duration: 0.22, ease: EASE, delay: 0.07 },
  },
};

export interface CaseStudyCardProps {
  slug: string;
  title: string;
  imageSrc: string;
  imageAlt: string;
  accentColor: 'orange' | 'blue' | 'cyan' | 'graphite';
  position: 1 | 2 | 3 | 4;
}

const CaseStudyCard = ({ slug, title, imageSrc, imageAlt, accentColor, position }: CaseStudyCardProps) => {
  const t = useTranslations('common');
  const cardRef = useRef<HTMLDivElement>(null);
  const isFirstHover = useRef(true);
  const [isHovered, setIsHovered] = useState(false);

  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const springConfig = { stiffness: 300, damping: 30, mass: 0.5 };
  const x = useSpring(mouseX, springConfig);
  const y = useSpring(mouseY, springConfig);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    mouseX.set(e.clientX);
    mouseY.set(e.clientY - 60);
  };

  const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isFirstHover.current) {
      x.jump(e.clientX);
      y.jump(e.clientY - 60);
      isFirstHover.current = false;
    } else {
      mouseX.set(e.clientX);
      mouseY.set(e.clientY - 60);
    }

    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  return (
    <>
      <motion.div
        ref={cardRef}
        className={`${styles.card} ${styles[`position${position}`]} ${styles[`accent-${accentColor}`]}`}
        variants={cardVariants}
        initial="idle"
        whileHover="hover"
        whileFocus="hover"
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        aria-label={title}
      >
        <Link href={{ pathname: '/work/[slug]', params: { slug } }} className={styles.link}>
          <Box className={styles.imageWrapper}>
            <Image
              src={imageSrc}
              alt={imageAlt}
              fill
              sizes="(max-width: 768px) 100vw, 2400px"
              quality={100}
              priority
              className={styles.image}
            />
          </Box>
        </Link>
      </motion.div>

      {isHovered && (
        <Portal>
          <motion.div
            className={styles.chipWrapper}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              x,
              y,
              zIndex: 9999,
              pointerEvents: 'none',
              whiteSpace: 'nowrap',
            }}
            variants={chipVariants}
            initial="idle"
            animate="hover"
            aria-hidden
          >
            <Chip
              label={
                <>
                  {title} {t('caseStudy')}
                  <Box component="span" className={styles.chipArrow}>
                    <External size={16} />
                  </Box>
                </>
              }
              classes={{ root: styles.chipRoot, label: styles.chipLabel }}
            />
          </motion.div>
        </Portal>
      )}
    </>
  );
};

export default CaseStudyCard;
